import { useState, useEffect } from "react";
import { Activity, HeartPulse, Wind, Thermometer, AlertTriangle, AlertCircle, CheckCircle2 } from "lucide-react";
import { UnifiedRecord } from "../../types";
import { useGlobalAlerts } from "../../contexts/AlertContext";

// Helper to generate bounded random walk for vitals
const jitter = (val: number, maxChange: number, min: number, max: number) => {
  const change = (Math.random() - 0.5) * maxChange;
  return Math.max(min, Math.min(max, val + change));
};

interface PatientVitals {
  patient_id: string;
  name: string;
  bed: string;
  heartRate: number;
  spo2: number;
  sysBP: number;
  temp: number;
  ewsScore: number;
}

function calculateMEWS(hr: number, spo2: number, sysBP: number, temp: number): number {
  let score = 0;
  
  // Heart Rate Score
  if (hr <= 40 || hr >= 130) score += 3;
  else if (hr >= 111 || hr <= 50) score += 2;
  else if (hr >= 91) score += 1;

  // SpO2 Score
  if (spo2 <= 91) score += 3;
  else if (spo2 <= 93) score += 2;
  else if (spo2 <= 95) score += 1;

  // Systolic BP Score
  if (sysBP <= 70) score += 3;
  else if (sysBP <= 80 || sysBP >= 200) score += 2;
  else if (sysBP <= 100) score += 1;

  // Temp Score
  if (temp <= 35.0 || temp >= 39.1) score += 2;
  else if (temp <= 36.0 || temp >= 38.1) score += 1;

  return score;
}

export function EarlyWarningSystem({ records }: { records: UnifiedRecord[] }) {
  const [vitalsData, setVitalsData] = useState<PatientVitals[]>([]);
  const { addAlert, activeAlerts } = useGlobalAlerts();

  // Initialize and update vitals
  useEffect(() => {
    // Only track patients with resolved beds
    const admittedPatients = records.filter(r => r.resolved_bed);
    
    // Initialize random realistic starting vitals
    let currentVitals = admittedPatients.map((r, i) => {
      // Intentionally make some patients critical for the demo
      const isCriticalDemo = i % 5 === 0; 
      
      const hr = isCriticalDemo ? 125 : 75 + Math.random() * 20;
      const spo2 = isCriticalDemo ? 89 : 96 + Math.random() * 3;
      const sysBP = isCriticalDemo ? 195 : 110 + Math.random() * 25;
      const temp = isCriticalDemo ? 39.2 : 36.5 + Math.random() * 1.0;
      
      return {
        patient_id: r.patient_id,
        name: r.patient_name || "Unknown Patient",
        bed: r.resolved_bed || "N/A",
        heartRate: hr,
        spo2: spo2,
        sysBP: sysBP,
        temp: temp,
        ewsScore: calculateMEWS(hr, spo2, sysBP, temp)
      };
    });
    
    setVitalsData(currentVitals);

    // Simulate real-time monitoring updates every 2 seconds
    const interval = setInterval(() => {
      setVitalsData(prev => {
        return prev.map(p => {
          const newHR = jitter(p.heartRate, 4, 40, 180);
          const newSpo2 = jitter(p.spo2, 1, 70, 100);
          const newSysBP = jitter(p.sysBP, 5, 60, 220);
          const newTemp = jitter(p.temp, 0.2, 34, 41);
          
          const ewsScore = calculateMEWS(newHR, newSpo2, newSysBP, newTemp);
          
          if (ewsScore >= 5) {
            // Check if there is already an active alert for this patient
            const hasAlert = activeAlerts.some(a => a.type === 'patient_critical' && a.metadata?.id === p.patient_id);
            if (!hasAlert) {
              addAlert({
                type: 'patient_critical',
                title: 'CRITICAL: Code Blue',
                message: `Patient ${p.name} (Bed ${p.bed}) requires immediate attention.`,
                metadata: { id: p.patient_id, bed: p.bed, mews: ewsScore }
              });
            }
          }

          return {
            ...p,
            heartRate: newHR,
            spo2: newSpo2,
            sysBP: newSysBP,
            temp: newTemp,
            ewsScore: ewsScore
          };
        }).sort((a, b) => b.ewsScore - a.ewsScore); // Always sort critical to top
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [records, activeAlerts, addAlert]);

  // If no patients, show placeholder
  if (vitalsData.length === 0) {
    return (
      <div className="bg-[#071428]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mt-6">
        <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-2">
          <Activity className="w-5 h-5 text-red-400" /> Vitals & Early Warning System (EWS)
        </h3>
        <p className="text-gray-500 text-sm">No admitted patients to monitor.</p>
      </div>
    );
  }

  // Get the most critical patient count
  const criticalCount = vitalsData.filter(v => v.ewsScore >= 5).length;
  const warningCount = vitalsData.filter(v => v.ewsScore >= 3 && v.ewsScore < 5).length;

  return (
    <div className="bg-[#071428]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mt-6 animate-fade-in shadow-[0_0_40px_rgba(220,38,38,0.1)]">
      
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-red-400" />
            Live Vitals & Early Warning System (EWS)
          </h3>
          <p className="text-gray-400 text-sm">Real-time automated deterioration detection (MEWS Algorithm)</p>
        </div>
        
        <div className="flex gap-3">
          <div className={`px-4 py-2 rounded-xl flex items-center gap-2 border ${criticalCount > 0 ? 'bg-red-500/20 border-red-500/40 text-red-400 animate-pulse' : 'bg-white/5 border-white/10 text-gray-400'}`}>
            <AlertTriangle className="w-4 h-4" />
            <span className="font-bold">{criticalCount} Critical</span>
          </div>
          <div className={`px-4 py-2 rounded-xl flex items-center gap-2 border ${warningCount > 0 ? 'bg-amber-500/20 border-amber-500/40 text-amber-400' : 'bg-white/5 border-white/10 text-gray-400'}`}>
            <AlertCircle className="w-4 h-4" />
            <span className="font-bold">{warningCount} Warning</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {vitalsData.slice(0, 9).map(patient => {
          
          let cardColor = "border-white/5 bg-white/5";
          let alertIcon = <CheckCircle2 className="w-5 h-5 text-emerald-400" />;
          let statusText = "Stable";
          let statusColor = "text-emerald-400";

          if (patient.ewsScore >= 5) {
            cardColor = "border-red-500/50 bg-red-500/10 shadow-[0_0_15px_rgba(220,38,38,0.2)]";
            alertIcon = <AlertTriangle className="w-5 h-5 text-red-400 animate-pulse" />;
            statusText = "CRITICAL DETERIORATION";
            statusColor = "text-red-400 font-bold";
          } else if (patient.ewsScore >= 3) {
            cardColor = "border-amber-500/40 bg-amber-500/10";
            alertIcon = <AlertCircle className="w-5 h-5 text-amber-400" />;
            statusText = "Observation Required";
            statusColor = "text-amber-400";
          }

          return (
            <div key={patient.patient_id} className={`rounded-xl border p-4 transition-colors duration-500 ${cardColor}`}>
              <div className="flex justify-between items-start mb-3 border-b border-white/5 pb-3">
                <div>
                  <div className="text-white font-bold text-sm">{patient.name}</div>
                  <div className="text-gray-400 text-xs">Bed: {patient.bed} | ID: {patient.patient_id}</div>
                </div>
                <div className="flex flex-col items-end">
                  {alertIcon}
                  <span className={`text-[10px] mt-1 ${statusColor}`}>{statusText}</span>
                </div>
              </div>

              {/* Vitals Grid */}
              <div className="grid grid-cols-2 gap-3">
                {/* Heart Rate */}
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-black/40 ${(patient.heartRate > 110 || patient.heartRate < 50) ? 'text-red-400' : 'text-blue-400'}`}>
                    <HeartPulse className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">HR (bpm)</div>
                    <div className={`font-mono font-bold text-sm ${(patient.heartRate > 110 || patient.heartRate < 50) ? 'text-red-400' : 'text-white'}`}>
                      {Math.round(patient.heartRate)}
                    </div>
                  </div>
                </div>

                {/* SpO2 */}
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-black/40 ${patient.spo2 < 93 ? 'text-red-400' : 'text-sky-400'}`}>
                    <Wind className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">SpO2 (%)</div>
                    <div className={`font-mono font-bold text-sm ${patient.spo2 < 93 ? 'text-red-400' : 'text-white'}`}>
                      {Math.round(patient.spo2)}%
                    </div>
                  </div>
                </div>

                {/* BP */}
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-black/40 ${(patient.sysBP > 180 || patient.sysBP < 90) ? 'text-red-400' : 'text-purple-400'}`}>
                    <Activity className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">BP Sys</div>
                    <div className={`font-mono font-bold text-sm ${(patient.sysBP > 180 || patient.sysBP < 90) ? 'text-red-400' : 'text-white'}`}>
                      {Math.round(patient.sysBP)}
                    </div>
                  </div>
                </div>

                {/* Temp */}
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-black/40 ${(patient.temp > 38.5 || patient.temp < 35.5) ? 'text-red-400' : 'text-orange-400'}`}>
                    <Thermometer className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Temp (°C)</div>
                    <div className={`font-mono font-bold text-sm ${(patient.temp > 38.5 || patient.temp < 35.5) ? 'text-red-400' : 'text-white'}`}>
                      {patient.temp.toFixed(1)}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t border-white/5 flex justify-between items-center">
                <span className="text-xs text-gray-500">MEWS Score</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-md bg-black/50 ${patient.ewsScore >= 5 ? 'text-red-400' : patient.ewsScore >= 3 ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {patient.ewsScore}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      {vitalsData.length > 9 && (
        <div className="text-center mt-4 pt-4 border-t border-white/5">
          <button className="text-sm text-teal-400 hover:text-teal-300 transition-colors">
            View All {vitalsData.length} Monitored Patients
          </button>
        </div>
      )}
    </div>
  );
}
